import type { ReactNode } from "react";
import { Calendar as CalendarPicker } from "@ascleo/ui/components/ui/calendar";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@ascleo/ui/components/ui/input-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ascleo/ui/components/ui/popover";
import { cn } from "@ascleo/ui/lib/utils";
import { de } from "date-fns/locale";
import { CalendarDays, Cake } from "lucide-react";
import { KioskFloatingLabel } from "../../../components/kiosk-field";
import { useTranslation } from "react-i18next";

// A calendar date, optionally followed by a time part. Card and FHIR reads can
// deliver a birth date as a midnight timestamp ("2000-10-01T00:00:00.000Z");
// only the calendar date is meaningful, so the time part is ignored rather
// than shown to the patient (KC-74).
const ISO_DATE_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const GERMAN_DATE_PATTERN = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

function padDatePart(value: number) {
  return String(value).padStart(2, "0");
}

function dateFromParts(year: number, month: number, day: number) {
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }

  return date;
}

/**
 * Converts a local Date into the ISO date string expected by patient forms.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * dateOfBirthToIso(new Date(1980, 0, 1));
 * ```
 */
export function dateOfBirthToIso(date: Date) {
  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join("-");
}

/**
 * Parses a patient birth date entered as ISO or DD.MM.YYYY.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * dateOfBirthToDate("01.01.1980");
 * ```
 */
export function dateOfBirthToDate(value: string) {
  const trimmed = value.trim();
  const isoMatch = ISO_DATE_PATTERN.exec(trimmed);

  if (isoMatch) {
    return dateFromParts(
      Number(isoMatch[1]),
      Number(isoMatch[2]),
      Number(isoMatch[3]),
    );
  }

  const germanMatch = GERMAN_DATE_PATTERN.exec(trimmed);

  if (!germanMatch) {
    return undefined;
  }

  return dateFromParts(
    Number(germanMatch[3]),
    Number(germanMatch[2]),
    Number(germanMatch[1]),
  );
}

/**
 * Formats a patient birth-date value for display in the kiosk input.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * formatDateOfBirthForDisplay("1980-01-01");
 * ```
 */
export function formatDateOfBirthForDisplay(value: string) {
  const date = dateOfBirthToDate(value);

  if (!date) {
    return value;
  }

  return [
    padDatePart(date.getDate()),
    padDatePart(date.getMonth() + 1),
    date.getFullYear(),
  ].join(".");
}

/**
 * Normalizes a patient birth-date value to the API date format.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * normalizeDateOfBirthForApi("01.01.1980");
 * ```
 */
export function normalizeDateOfBirthForApi(value: string) {
  const date = dateOfBirthToDate(value);
  return date ? dateOfBirthToIso(date) : value.trim();
}

/**
 * Renders a non-native patient birth-date input with typed and calendar entry,
 * following the kiosk input standard: a 56px `InputGroup` with a leading
 * `Cake` icon, the typed `TT.MM.JJJJ` input and the calendar button inside the
 * group (FI-05). Pass `floatingLabel` to render the label inside the group, and
 * wrap it in `KioskFieldFrame` for description and error; `KioskDateOfBirthField`
 * in `components/kiosk-date-of-birth-field.tsx` does both. Without a floating
 * label the format placeholder stays visible.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <DateOfBirthInput dataTestId="dob-input" value="" onChange={() => undefined} />
 * ```
 */
export function DateOfBirthInput({
  dataTestId,
  pickerDataTestId,
  id,
  name,
  value,
  onChange,
  onBlur,
  disabled,
  required,
  floatingLabel,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: {
  dataTestId: string;
  pickerDataTestId?: string;
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  /**
   * @deprecated Ignored. Kiosk inputs are always 56px tall (touch-target
   * minimum); remove the prop at call sites.
   */
  size?: "xl" | "2xl";
  /** Label rendered as the group's floating label; requires `id`. */
  floatingLabel?: ReactNode;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}) {
  const { t } = useTranslation();
  const selectedDate = dateOfBirthToDate(value);

  return (
    <InputGroup size="2xl">
      <InputGroupAddon align="inline-start" className="ps-4">
        <Cake aria-hidden className="size-6" />
      </InputGroupAddon>
      <InputGroupInput
        data-testid={dataTestId}
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="bday"
        placeholder={t("self-service.check-in.patient-lookup.dob-placeholder")}
        value={formatDateOfBirthForDisplay(value)}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => {
          onChange(normalizeDateOfBirthForApi(value));
          onBlur?.();
        }}
        disabled={disabled}
        required={required}
        aria-required={required}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className="h-full text-lg"
      />
      {floatingLabel !== undefined && id !== undefined ? (
        <KioskFloatingLabel htmlFor={id} required={required}>
          {floatingLabel}
        </KioskFloatingLabel>
      ) : null}
      <InputGroupAddon align="inline-end" className="pe-1.5">
        <Popover>
          <PopoverTrigger
            render={
              <InputGroupButton
                data-testid={pickerDataTestId ?? `${dataTestId}-picker-btn`}
                variant="ghost"
                className="size-12 rounded-xl p-0"
                disabled={disabled}
                aria-label={t("self-service.check-in.patient-lookup.dob-picker")}
              />
            }
          >
            <CalendarDays aria-hidden className="size-6" />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="end">
            <CalendarPicker
              mode="single"
              selected={selectedDate}
              defaultMonth={selectedDate ?? new Date(1980, 0, 1)}
              captionLayout="dropdown"
              startMonth={new Date(1900, 0, 1)}
              endMonth={new Date()}
              locale={de}
              onSelect={(date) => {
                if (date) {
                  onChange(dateOfBirthToIso(date));
                  onBlur?.();
                }
              }}
              className={cn("[--cell-size:--spacing(12)]")}
            />
          </PopoverContent>
        </Popover>
      </InputGroupAddon>
    </InputGroup>
  );
}
